import React, { useState } from 'react';
import {
    Container,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Breadcrumbs,
    Link
} from '@mui/material';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';
import { detectPlatform } from '../../utils/platformDetection';
import { downloadLinkFor, trackStoreClick } from '../../utils/appStoreLinks';
import { blogPosts, BLOG_CATEGORIES as categories } from '../../content/blogPosts';
import {
    Search as SearchIcon,
    ArrowBack,
    AccessTime,
    Person,
    Favorite,
    TrendingUp,
    Psychology,
    Security,
    Group,
    EmojiObjects
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LIGHT_PURPLE = '#A76FFF';
const VIBRANT_RED = '#FF5A76';


const BlogScreen: React.FC = () => {
    const navigate = useNavigate();
    const { platform } = detectPlatform();
    const download = downloadLinkFor(platform, 'page');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');

    const filteredPosts = blogPosts.filter(post => {
        const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
        const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    const featuredPosts = blogPosts.filter(post => post.featured);

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'Dating Advice':
                return <Favorite sx={{ color: VIBRANT_RED, fontSize: 20 }} />;
            case 'Safety':
                return <Security sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
            case 'Mental Health':
                return <Psychology sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
            case 'Trends':
                return <TrendingUp sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
            case 'Relationships':
                return <Group sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
            default:
                return <EmojiObjects sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
        }
    };

    return (
        <>
            <SEOHead
                title="Dating Blog & Relationship Advice - Pop The Balloon"
                description="Expert dating advice, relationship tips, and safety guides. Learn how to build authentic connections and navigate modern dating successfully."
                canonical="https://www.poptheballoonapp.com/blog"
                ogTitle="Dating Blog & Expert Relationship Advice"
                ogDescription="Get expert advice on dating, relationships, and building authentic connections. Tips for modern dating success and safety."
                ogUrl="https://www.poptheballoonapp.com/blog"
            />

            <SiteHeader />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                        <Link
                            color="inherit"
                            href="/"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/');
                            }}
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Home
                        </Link>
                        <Typography color="text.primary">Blog</Typography>
                    </Breadcrumbs>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: LIGHT_PURPLE }}>
                            Dating & Relationship Blog
                        </Typography>
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px' }}>
                        Expert advice, safety tips, and insights to help you navigate modern dating and build meaningful relationships.
                    </Typography>
                </Box>

                {/* Search and Filters */}
                <Box sx={{ mb: 4 }}>
                    <TextField
                        fullWidth
                        placeholder="Search articles..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3, maxWidth: '500px' }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {categories.map((category) => (
                            <Chip
                                key={category}
                                label={category}
                                onClick={() => setSelectedCategory(category)}
                                variant={selectedCategory === category ? 'filled' : 'outlined'}
                                sx={{
                                    bgcolor: selectedCategory === category ? LIGHT_PURPLE : 'transparent',
                                    color: selectedCategory === category ? 'white' : LIGHT_PURPLE,
                                    borderColor: LIGHT_PURPLE,
                                    '&:hover': {
                                        bgcolor: selectedCategory === category ? '#8A5CF5' : 'rgba(167, 111, 255, 0.1)',
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Featured Articles */}
                {selectedCategory === 'All' && searchTerm === '' && (
                    <Box sx={{ mb: 6 }}>
                        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                            <EmojiObjects sx={{ mr: 2, color: LIGHT_PURPLE }} />
                            Featured Articles
                        </Typography>
                        <Grid container spacing={3}>
                            {featuredPosts.map((post) => (
                                <Grid item xs={12} md={6} key={post.id}>
                                    <Card 
                                        sx={{ 
                                            height: '100%', 
                                            cursor: 'pointer',
                                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 8px 25px rgba(167, 111, 255, 0.15)'
                                            }
                                        }}
                                        onClick={() => navigate(`/blog/${post.id}`)}
                                    >
                                        <CardMedia
                                            component="div"
                                            sx={{
                                                height: 200,
                                                background: `linear-gradient(45deg, ${LIGHT_PURPLE}, ${VIBRANT_RED})`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography variant="h6" sx={{ color: 'white', textAlign: 'center', px: 2 }}>
                                                {post.title}
                                            </Typography>
                                        </CardMedia>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                                                {getCategoryIcon(post.category)}
                                                <Chip
                                                    label={post.category}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'rgba(167, 111, 255, 0.1)',
                                                        color: LIGHT_PURPLE
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                                {post.excerpt}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'text.secondary' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Person fontSize="small" />
                                                    <Typography variant="caption">{post.author}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AccessTime fontSize="small" />
                                                    <Typography variant="caption">{post.readTime}</Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* All Articles */}
                <Box>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                        {selectedCategory === 'All' ? 'All Articles' : `${selectedCategory} Articles`}
                        {searchTerm && ` - "${searchTerm}"`}
                    </Typography>
                    
                    {filteredPosts.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="h6" color="text.secondary">
                                No articles found matching your search.
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('All');
                                }}
                                sx={{ mt: 2, borderColor: LIGHT_PURPLE, color: LIGHT_PURPLE }}
                            >
                                Clear Filters
                            </Button>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {filteredPosts.map((post) => (
                                <Grid item xs={12} md={6} lg={4} key={post.id}>
                                    <Card 
                                        sx={{ 
                                            height: '100%', 
                                            cursor: 'pointer',
                                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 8px 25px rgba(167, 111, 255, 0.15)'
                                            }
                                        }}
                                        onClick={() => navigate(`/blog/${post.id}`)}
                                    >
                                        <CardMedia
                                            component="div"
                                            sx={{
                                                height: 160,
                                                background: `linear-gradient(45deg, ${LIGHT_PURPLE}, ${VIBRANT_RED})`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {getCategoryIcon(post.category)}
                                        </CardMedia>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                                <Chip
                                                    label={post.category}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'rgba(167, 111, 255, 0.1)',
                                                        color: LIGHT_PURPLE
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.3 }}>
                                                {post.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                                {post.excerpt}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'text.secondary' }}>
                                                <Typography variant="caption">{post.author}</Typography>
                                                <Typography variant="caption">{post.readTime}</Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>

                {/* Call to Action */}
                <Box sx={{
                    textAlign: 'center',
                    py: 6,
                    mt: 6,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, rgba(167, 111, 255, 0.1), rgba(255, 90, 118, 0.1))`,
                    border: `1px solid rgba(167, 111, 255, 0.2)`
                }}>
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                        Ready to Start Dating?
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '500px', mx: 'auto' }}>
                        Put these tips into practice and find meaningful connections on Pop The Balloon.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<Favorite />}
                        href={download.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackStoreClick(download.store, 'page', platform)}
                        sx={{
                            bgcolor: LIGHT_PURPLE,
                            px: 6,
                            py: 2,
                            fontSize: '1.2rem',
                            borderRadius: 3,
                            '&:hover': {
                                bgcolor: '#8A5CF5',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 25px rgba(167, 111, 255, 0.4)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Join Pop The Balloon
                    </Button>
                </Box>
            </Container>
            <AppFooter />
        </>
    );
};

export default BlogScreen;